import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import userAPI from "../../api/UserApi";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { FiDownload, FiTruck, FiLayers, FiInfo, FiCreditCard, FiUsers } from "react-icons/fi";

function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState({});
  const [orderItems, setOrderItems] = useState([]);

  if (!id) {
    navigate("/");
  }

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const res = await userAPI.orderDetails(id);
        if (res.code === 200) {
          setOrder(res.data);
          setOrderItems(res.data?.items || []);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchOrderDetails();
  }, [id]);

  const handleDownload = async (orderId) => {
    const element = document.querySelector(".invoice-wrapper-funky");
    window.scrollTo(0, 0);
    await new Promise((resolve) => setTimeout(resolve, 800));

    const canvas = await html2canvas(element, {
      scale: 2, // 👈 Higher scale means razor-sharp PDF print text
      useCORS: true,
      allowTaint: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pageWidth;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    if (pdfHeight > pageHeight) {
      let position = 0;
      while (position < pdfHeight) {
        pdf.addImage(imgData, "PNG", 0, -position, pdfWidth, pdfHeight);
        position += pageHeight;
        if (position < pdfHeight) pdf.addPage();
      }
    } else {
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    }

    pdf.save("Invoice_" + orderId + ".pdf");
  };

  return (
    <div className="order-details-page-main py-4">
      <div className="container">
        {/* TOP CONTROL ACTION BAR */}
        <div className="d-flex justify-content-end gap-3 mb-4 no-print">
          <button
            className="funky-action-btn-outline"
            onClick={() => handleDownload(order?.orderId)}
          >
            <FiDownload className="me-2" /> Download Invoice
          </button>

          <button className="funky-action-btn-solid">
            <FiTruck className="me-2" /> Track Shipment
          </button>
        </div>

        {/* MAIN INVOICE SHEET CONTAINER */}
        <div className="invoice-wrapper-funky p-4 p-md-5">
          <div className="row g-4">
            {/* 1. ORDER BASE METRICS INFO */}
            <div className="col-12">
              <div className="funky-invoice-card">
                <div className="card-header-sticker">
                  <FiLayers /> Order Information
                </div>
                <div className="card-body-inner mt-4">
                  <div className="row g-3">
                    <div className="col-md-4 col-6">
                      <small className="meta-label-text">Order ID</small>
                      <h5 className="meta-value-bold">#{order?.orderId}</h5>
                    </div>
                    <div className="col-md-4 col-6">
                      <small className="meta-label-text">Order Date</small>
                      <h6 className="meta-value-text">
                        {order?.orderDate ? new Date(order?.orderDate).toLocaleString() : "N/A"}
                      </h6>
                    </div>
                    <div className="col-md-4 col-12">
                      <small className="meta-label-text">Total Amount</small>
                      <h5 className="meta-value-bold text-purple-fill">₹{order?.total}/-</h5>
                    </div>
                    <div className="col-6 col-md-4 mt-3">
                      <small className="meta-label-text">Order Status</small>
                      <div>
                        <span className="status-badge-funky status-pending">
                          {order?.status || "PROCESSING"}
                        </span>
                      </div>
                    </div>
                    <div className="col-6 col-md-4 mt-3">
                      <small className="meta-label-text">Payment Status</small>
                      <div>
                        <span className="status-badge-funky status-success">
                          {order?.payment?.status || "PAID"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. ITEM LIST DISPLAY (WITH 72x94 PORTRAIT RATIO) */}
            <div className="col-12">
              <div className="funky-invoice-card">
                <div className="card-header-sticker">
                  <FiInfo /> Item's Information
                </div>
                
                <div className="table-responsive mt-4">
                  <table className="funky-invoice-table">
                    <thead>
                      <tr>
                        <th>Item Description</th>
                        <th>MRP</th>
                        <th>Our Price</th>
                        <th className="text-center">Qty</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems?.map((item, index) => (
                        <tr key={item._id || index}>
                          <td>
                            <div className="invoice-product-cell-wrapper">
                              {/* Exact Custom Portrait Dimensions Box */}
                              <div className="invoice-product-img-box-72x94">
                                <img
                                  src={item?.thumbnail || item?.image}
                                  alt={item?.productName}
                                />
                              </div>
                              <div className="invoice-product-meta-desc">
                                <h6 className="invoice-item-title-name">{item?.productName}</h6>
                                {item?.variantName && (
                                  <span className="invoice-variant-tag">{item?.variantName}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-muted-strike">₹{item?.mrp}/-</td>
                          <td className="price-bold-text">₹{item?.price}/-</td>
                          <td className="text-center fw-bold">{item?.quantity}</td>
                          <td className="text-end price-bold-text">
                            ₹{item?.price * item?.quantity}/-
                          </td>
                        </tr>
                      ))}

                      {/* SUMMARY SUBTOTAL CLOSURES */}
                      <tr className="summary-split-row">
                        <td colSpan={4} className="text-end summary-label">Subtotal</td>
                        <td className="text-end summary-value">₹{order?.subtotal}/-</td>
                      </tr>
                      <tr className="summary-final-row">
                        <td colSpan={4} className="text-end final-label">Grand Total</td>
                        <td className="text-end final-value">₹{order?.total}/-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 3. PAYMENT GATEWAY STICKERS */}
            <div className="col-md-6">
              <div className="funky-invoice-card h-100">
                <div className="card-header-sticker">
                  <FiCreditCard /> Payment Details
                </div>
                <div className="card-body-inner mt-4">
                  <ul className="invoice-details-list">
                    <li><span>Payment Type:</span> <strong>{order?.payment?.method || "ONLINE"}</strong></li>
                    <li><span>Payment Status:</span> <strong className="text-success">{order?.payment?.status || "SUCCESS"}</strong></li>
                    <li><span>Time Trace:</span> <strong>{order?.payment?.paidAt ? new Date(order?.payment?.paidAt).toLocaleString() : "N/A"}</strong></li>
                    <li><span>Gateway ID:</span> <code className="invoice-code-trace">{order?.payment?.details?.gatewayOrderId || "N/A"}</code></li>
                    <li><span>Transaction ID:</span> <code className="invoice-code-trace">{order?.payment?.details?.id || "N/A"}</code></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 4. CUSTOMER SHIPPING METRICS */}
            <div className="col-md-6">
              <div className="funky-invoice-card h-100">
                <div className="card-header-sticker">
                  <FiUsers /> Customer Information
                </div>
                <div className="card-body-inner mt-4">
                  <ul className="invoice-details-list">
                    <li><span>Recipient:</span> <strong>{order?.user?.firstName} {order?.user?.lastName}</strong></li>
                    <li><span>Email Box:</span> <strong>{order?.user?.email}</strong></li>
                    <li><span>Phone Contact:</span> <strong>{order?.user?.mobile}</strong></li>
                    <li className="flex-column align-items-start gap-1">
                      <span>Shipping Destination:</span>
                      <strong className="shipping-address-block-text mt-1">
                        {[
                          order?.address?.addressLine1,
                          order?.address?.addressLine2,
                          order?.address?.district,
                          order?.address?.state,
                          order?.address?.pinCode,
                          order?.address?.country,
                        ].filter(Boolean).join(", ")}
                      </strong>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;